import type { AxiosInstance } from 'axios';
import type {
  ReviewSession,
  ReviewActivity,
  DocumentReview,
  ReviewAnnotation,
  ReviewAnnotationComment,
} from '../types/review';
import type { OnlyOfficeConfig } from './editorService';

export function createReviewPublicService(client: AxiosInstance) {
  return {
    loadSession: (token: string) =>
      client.get<ReviewSession>(`/v1/review-session/${token}`).then(r => r.data),

    markDone: (token: string) =>
      client.post(`/v1/review-session/${token}/done`),

    submitChanges: (token: string, message?: string) =>
      client.post(`/v1/review-session/${token}/submit`, { message }),

    markReviewerComplete: (token: string) =>
      client.post(`/v1/review-session/${token}/complete`),

    getSessionActivities: (token: string) =>
      client.get<ReviewActivity[]>(`/v1/review-session/${token}/activities`).then(r => r.data),

    getReviewEditorConfig: (token: string, documentId?: string) =>
      client.get<OnlyOfficeConfig>(`/v1/review-session/${token}/editor-config`, {
        params: { documentId },
      }).then(r => r.data),

    addReviewerNote: (token: string, message: string) =>
      client.post(`/v1/review-session/${token}/note`, { message }),

    getReviewHistory: (token: string) =>
      client.get<DocumentReview[]>(`/v1/review-session/${token}/history`).then(r => r.data),

    // ── PDF review annotations (token-scoped reviewer) ───────────────────

    listAnnotationsByToken: (token: string) =>
      client
        .get<ReviewAnnotation[]>(`/v1/review-session/${token}/annotations`)
        .then(r => r.data),

    createAnnotation: (token: string, annotation: Partial<ReviewAnnotation>) =>
      client
        .post<ReviewAnnotation>(`/v1/review-session/${token}/annotations`, annotation)
        .then(r => r.data),

    updateAnnotation: (token: string, annotationId: string, annotation: Partial<ReviewAnnotation>) =>
      client
        .put<ReviewAnnotation>(
          `/v1/review-session/${token}/annotations/${annotationId}`,
          annotation,
        )
        .then(r => r.data),

    deleteAnnotationAsReviewer: (token: string, annotationId: string) =>
      client.delete(`/v1/review-session/${token}/annotations/${annotationId}`),

    // ── Annotation comment threads (token-scoped reviewer) ───────────────

    listAnnotationCommentsByToken: (token: string, annotationId: string) =>
      client
        .get<ReviewAnnotationComment[]>(
          `/v1/review-session/${token}/annotations/${annotationId}/comments`,
        )
        .then(r => r.data),

    addAnnotationCommentByToken: (token: string, annotationId: string, message: string) =>
      client
        .post<ReviewAnnotationComment>(
          `/v1/review-session/${token}/annotations/${annotationId}/comments`,
          { message },
        )
        .then(r => r.data),

    deleteAnnotationCommentAsReviewer: (token: string, commentId: string) =>
      client.delete(`/v1/review-session/${token}/annotations/comments/${commentId}`),
  };
}
